import { Component, OnInit } from '@angular/core';
import { ComponentBase } from 'src/app/main/dynamic/component-base';
import { WorkflowGatewayService } from 'src/app/service/workflow-gateway.service';
import { PagingContainerModel } from 'src/app/model/paging-container.model';
import { AttributeModel } from 'src/app/main/dynamic/model/attribute.model';
import { SearchParamsModel } from 'src/app/model/search-params.model';
import { Observable, Subject, of } from 'rxjs';
import { catchError, debounceTime, map, switchMap } from 'rxjs/operators';

@Component({
  selector: 'app-process-select',
  templateUrl: './process-select.component.html',
  styleUrls: ['./process-select.component.scss']
})
export class ProcessSelectComponent extends ComponentBase implements OnInit {

  values: AttributeModel[] = [];
  filteredValues: AttributeModel[] = [];
  private searchSubject = new Subject<string>();

  constructor(
    private workflowService: WorkflowGatewayService) {
    super()
  }

  ngOnInit() {
    if (this.attribute.value == "") {
      this.attribute.value = null;
    }

    let policyId = this.attribute.options
      .find(opt => opt.key.toLowerCase() == "policyid");

    if (policyId) {
      this.fetchInitialProcesses(policyId.value);
    }

    this.searchSubject.pipe(
      debounceTime(500),
      switchMap(searchText => this.fetchProcesses(searchText))
    ).subscribe(filtered => {
      this.filteredValues = filtered;
      this.loadSelectedOption();
    });
  }

  fetchInitialProcesses(policyId: string) {
    let searchParamsModel: SearchParamsModel = {
      policyId: policyId,
      attributes: []
    };

    let filterSearchParams = this.attribute.attributes
      .find(attr => attr.type.toLowerCase() == "FilterSearchParams".toLowerCase());

    if (filterSearchParams) {
      filterSearchParams.attributes.forEach(attr => {
        if (attr.key.toLowerCase() == "status") {
          searchParamsModel.status = attr.value;
        } else {
          searchParamsModel.attributes.push(attr);
        }
      });
    }

    this.workflowService
      .getSummary(1, 20, { searchParams: searchParamsModel })
      .subscribe((pagingContainer: PagingContainerModel) => {
        this.values = pagingContainer.items.map(item => {
          return {
            key: item.id,
            value: item.attributes[0] && item.attributes[0].value
              ? item.attributes[0].value
              : item.id,
            type: "Text",
            attributes: [],
            options: []
          };
        });
        this.filteredValues = this.values;
        this.loadSelectedOption();
      });
  }

  private loadSelectedOption() {
    if (this.attribute.value) {
      let selected = this.attribute.attributes
        .find(attr => attr.type.toLowerCase() == 'processselectedoption');

      if (selected && !this.filteredValues.some(i => i.value.toLowerCase() == selected.value.toLowerCase())) {
        let newSelected = {
          key: this.attribute.value,
          value: selected.value,
          type: "Text",
          attributes: [],
          options: []
        };

        this.filteredValues = [newSelected, ...this.filteredValues];
      }
    }
  }

  fetchProcesses(searchText: string): Observable<any[]> {
    if (!searchText) {
      return new Observable<any[]>(observer => observer.next([]));
    }

    let policyId = this.attribute.options
      .find(opt => opt.key.toLowerCase() == "policyid");

    if (!policyId) {
      return new Observable<any[]>(observer => observer.next([]));
    }

    let searchParamsModel: SearchParamsModel = {
      policyId: policyId.value,
      attributes: [],
      nome: searchText
    };

    let filterSearchParams = this.attribute.attributes
      .find(attr => attr.type.toLowerCase() == "FilterSearchParams".toLowerCase());

    if (filterSearchParams) {
      filterSearchParams.attributes.forEach(attr => {
        if (attr.key.toLowerCase() == "status") {
          searchParamsModel.status = attr.value;
        } else {
          searchParamsModel.attributes.push(attr);
        }
      });
    }

    return this.workflowService.getSummary(1, 20, { searchParams: searchParamsModel })
      .pipe(
        map((pagingContainer: PagingContainerModel) => {
          return pagingContainer.items.map(item => {
            return {
              key: item.id,
              value: item.attributes[0] && item.attributes[0].value
                ? item.attributes[0].value
                : item.id,
              type: "Text",
              attributes: [],
              options: []
            };
          });
        }),
        catchError(() => of([]))
      );
  }

  onSearch(event: any): void {
    if (event.term.length >= 2) {
      this.searchSubject.next(event.term);
    } else {
      this.filteredValues = this.values;
      this.loadSelectedOption();
    }
  }

  getValue(): string {
    if (this.attribute.value) {
      let selected = this.attribute.attributes
        .find(attr => attr.type.toLowerCase() == 'processselectedoption');

      if (selected) {
        return selected.value;
      }
    }

    return "";
  }

  onSelectChanged() {
    if (!this.filteredValues) {
      return;
    }

    let selectingOption = this.filteredValues
      .find(attr => attr.key == this.attribute.value);

    if (selectingOption) {
      let selected = this.attribute.attributes
        .find(attr => attr.type.toLowerCase() == "processselectedoption");

      if (!selected) {
        selected = {
          key: `${this.attribute.key} Selected`,
          value: selectingOption.value,
          type: "ProcessSelectedOption",
          attributes: [],
          options: []
        };

        this.attribute.attributes.push(selected);
      } else {
        selected.value = selectingOption.value;
      }

      selected.attributes = [selectingOption];
    } else {
      let index = this.attribute.attributes
        .findIndex(attr => attr.type.toLowerCase() == 'processselectedoption');

      if (index != -1) {
        this.attribute.attributes.splice(index, 1);
      }
    }

    this.emit({ name: "autosave", data: "silently" });
  }

  get placeholder(): string {
    let placeholderOpt = this.attribute.options.find(option => option.key.toLowerCase() == "placeholder");

    if (placeholderOpt) {
      return placeholderOpt.value;
    }

    return "";
  }
}